import { useContext } from 'react'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCampaignQuoteTemplate, { useCampaignQuoteTemplateForm } from '@hooks/useCampaignQuoteTemplate'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import EntitySelector from '@components/EntitySelector'
import ToggleListItem from '@components/ToggleListItem'

import AssignedUsers from './components/AssignedUsers'

const MODAL_KEY = 'CreateOrEditCampaignQuoteTemplateModal'

type ModalCallbacks = {
  closeModal: () => void,
  createCampaignQuoteTemplate: (payload: HandleCallbackActionParams) => void,
  updateCampaignQuoteTemplate: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCampaignQuoteTemplateModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createCampaignQuoteTemplate, updateCampaignQuoteTemplate } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const campaignQuoteTemplate = digObject(modalPayload, 'campaignQuoteTemplate', {})

  const newRecord = !campaignQuoteTemplate.id

  const {
    callbacks: {
      createCampaignQuoteTemplate: createFn,
      updateCampaignQuoteTemplate: updateFn,
    },
    creating,
    updating,
  } = useCampaignQuoteTemplate(campaignQuoteTemplate)

  const campaignQuoteTemplateFormPayload = useCampaignQuoteTemplateForm(campaignQuoteTemplate, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      agency_id,
      disable_custom_items,
      disable_price_override,
      disable_section_editing,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = campaignQuoteTemplateFormPayload

  const createOrUpdateCampaignQuoteTemplatePayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    shouldRedirect: true,
    toastText: `Campaign Quote Template ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Quote Template"
      />

      <SidebarModal.Content>
        <Form>
          <EntitySelector
            callbacks={{
              selectItem: agency => setEntityState({ agency_id: agency ? agency.id : null }),
            }}
            entityKey="agencies"
            label="* Agency"
            // @ts-expect-error TS2322
            marginTop={['large', 0]}
            selectedItemId={agency_id}
          />

          <Form.Field label="* Title" errorMessage={errors.title} marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'large' }}>Settings</SectionDivider>

          <ToggleListItem
            onClick={() => setEntityState({ disable_section_editing: !disable_section_editing })}
            toggleOffColor="bodyFontLightColor"
            toggleOffText="No"
            toggleOnColor="blue"
            toggleOnText="Yes"
            toggleValue={disable_section_editing}
          >
            <ToggleListItem.Title>Lock Sections</ToggleListItem.Title>
            <ToggleListItem.Description>
              When enabled sections cannot be edited or moved when used in a quote.
            </ToggleListItem.Description>
          </ToggleListItem>

          <ToggleListItem
            onClick={() => setEntityState({ disable_custom_items: !disable_custom_items })}
            toggleOffColor="bodyFontLightColor"
            toggleOffText="No"
            toggleOnColor="blue"
            toggleOnText="Yes"
            toggleValue={disable_custom_items}
          >
            <ToggleListItem.Title>Disable Custom Items</ToggleListItem.Title>
            <ToggleListItem.Description>
              When enabled custom items cannot be added to the quote.
            </ToggleListItem.Description>
          </ToggleListItem>

          <ToggleListItem
            onClick={() => setEntityState({ disable_price_override: !disable_price_override })}
            toggleOffColor="bodyFontLightColor"
            toggleOffText="No"
            toggleOnColor="blue"
            toggleOnText="Yes"
            toggleValue={disable_price_override}
          >
            <ToggleListItem.Title>Disable Price Adjustments</ToggleListItem.Title>
            <ToggleListItem.Description>
              When enabled users cannot override quote item prices.
            </ToggleListItem.Description>
          </ToggleListItem>

          <SidebarModal.Separator />

          <AssignedUsers campaignQuoteTemplateFormPayload={campaignQuoteTemplateFormPayload} />
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCampaignQuoteTemplate(createOrUpdateCampaignQuoteTemplatePayload)
              : () => updateCampaignQuoteTemplate(createOrUpdateCampaignQuoteTemplatePayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Campaign Quote Template` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCampaignQuoteTemplateModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
